import {
  Appliance,
  ApplianceType,
  AppliancesFilters,
  NeedsAttentionReason,
} from '@hakimo-ui/hakimo/types';
import { Cell, Column, Row, TableData } from '@hakimo-ui/hakimo/ui-table';
import { ChevronDoubleRightIcon } from '@heroicons/react/24/solid';
import dayjs from 'dayjs';
import { Link } from 'react-router-dom';

export const PAGE_SIZE = 20;

export const defaultFilters: AppliancesFilters = {
  applianceType: [],
  tenantId: [],
  needsAttention: [],
};

export const APPLIANCE_TYPE_LABELS: Record<ApplianceType, string> = {
  MAC_MINI: 'Mac Mini',
  MAC_STUDIO: 'Mac Studio',
  DELL: 'Dell',
  BEELINK: 'Beelink',
  RASPI: 'Raspberry Pi',
  CUSTOM_VM: 'Custom VM',
};

export const APPLIANCE_TYPE_OPTIONS = Object.keys(
  APPLIANCE_TYPE_LABELS
) as ApplianceType[];

export const NEEDS_ATTENTION_OPTIONS: {
  id: NeedsAttentionReason;
  label: string;
}[] = [
  { id: 'no_frp_port', label: 'No FRP port' },
  { id: 'no_location', label: 'No location' },
  { id: 'no_serial', label: 'No serial' },
  { id: 'no_appliance_id', label: 'No appliance ID' },
  { id: 'duplicate_openvpn_ip', label: 'Duplicate VPN IP' },
];

export const isApplianceType = (value: string): value is ApplianceType =>
  APPLIANCE_TYPE_OPTIONS.includes(value as ApplianceType);

export const isNeedsAttentionReason = (
  value: string
): value is NeedsAttentionReason =>
  NEEDS_ATTENTION_OPTIONS.some((option) => option.id === value);

export const humanizeType = (type: ApplianceType) =>
  APPLIANCE_TYPE_LABELS[type] || type;

export const dash = (value: string | number | null | undefined) =>
  value === null || value === undefined || value === '' ? '—' : value;

export const formatDateTime = (value: string | null | undefined) =>
  value ? dayjs(value).format('DD/MM/YYYY, HH:mm:ss') : '—';

export const formatDate = (value: string | null | undefined) =>
  value ? dayjs(value).format('DD/MM/YYYY') : '—';

export const statusDotClass = (hostStatus: string | null) =>
  hostStatus === 'online' ? 'bg-status-green' : 'bg-status-gray';

const StatusDot = ({ status }: { status: string | null }) => (
  <span className="flex items-center gap-1.5">
    <span className={`h-2 w-2 rounded-full ${statusDotClass(status)}`} />
    <span>{dash(status)}</span>
  </span>
);

const WrappedText = ({ value }: { value: string | number }) => (
  <span className="whitespace-normal">{value}</span>
);

export const applianceColumns: Column[] = [
  { id: 'appliance', name: 'Appliance' },
  { id: 'type', name: 'Type' },
  { id: 'status', name: 'Status' },
  { id: 'tenant', name: 'Tenant' },
  { id: 'location', name: 'Location' },
  { id: 'openvpnIp', name: 'VPN IP' },
  { id: 'frpPort', name: 'FRP port' },
  { id: 'lastSeen', name: 'Last seen' },
];
export const tenantApplianceColumns: Column[] = [
  ...applianceColumns,
  { id: 'actions', name: '' },
];

export function getTableData(
  appliances: Appliance[],
  onClickRow: (
    id: string,
    event: React.MouseEvent | React.KeyboardEvent
  ) => void,
  withChevron = false
): TableData {
  return {
    columns: withChevron ? tenantApplianceColumns : applianceColumns,
    rows: appliances.map((appliance) => {
      const tenantCell =
        appliance.tenantId && appliance.tenantName ? (
          <Link
            to={`/tenants-admin/${appliance.tenantId}`}
            onClick={(e) => e.stopPropagation()}
            className="text-primary-500 hover:underline"
          >
            {appliance.tenantName}
          </Link>
        ) : (
          dash(appliance.tenantName)
        );

      const cells: Cell[] = [
        { value: <WrappedText value={appliance.applianceName} /> },
        { value: humanizeType(appliance.applianceType) },
        { value: <StatusDot status={appliance.hostStatus} /> },
        { value: tenantCell },
        { value: dash(appliance.physicalLocationName) },
        { value: <WrappedText value={dash(appliance.openvpnIp)} /> },
        { value: dash(appliance.frpPort) },
        { value: formatDateTime(appliance.lastSeenAt) },
      ];
      if (withChevron) {
        cells.push({
          value: (
            <ChevronDoubleRightIcon className="flex h-5 w-5 flex-shrink-0 items-center justify-between" />
          ),
        });
      }
      const row: Row = {
        id: String(appliance.id),
        cells,
        onClick: (event) => onClickRow(String(appliance.id), event),
      };
      return row;
    }),
  };
}

export function hasFiltersApplied(filters: AppliancesFilters) {
  return (
    filters.applianceType.length > 0 ||
    filters.tenantId.length > 0 ||
    filters.needsAttention.length > 0
  );
}
